import {
  Box,
  Checkbox,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useTheme,
} from '@chakra-ui/react';
import {
  mdiArrowDown,
  mdiArrowUp,
  mdiChevronDown,
  mdiChevronUp,
  mdiDotsHorizontal,
} from '@mdi/js';
import React, { useEffect } from 'react';
import {
  Hooks,
  useExpanded,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { Pagination } from '../Pagination/Pagination';
import { PortalIcon } from '../PortalIcon';
import {
  DataTableColumn,
  DataTableProps,
  DataTableRowAction,
  DataTableRowActionCallback,
  DataTableSortingRule,
} from './model';

/**
 * Internal function for filtering out hidden columns
 */
const hideColumnHook = (hooks: Hooks) => {
  hooks.visibleColumns.push((columns) =>
    columns.filter((column: any) => !column.hideColumn),
  );
};

/**
 * Internal function for generating the rowAction menu column
 */

const getRowActionsAriaLabel = (row: {
  original?: Record<string, unknown>;
}) => {
  const original = row.original;
  if (!original) {
    return 'Open row actions menu';
  }

  const givenName = original.givenName;
  const familyName = original.familyName;
  if (typeof givenName === 'string' && typeof familyName === 'string') {
    return `Actions for ${givenName} ${familyName}`;
  }

  if (typeof original.name === 'string') {
    return `Actions for ${original.name}`;
  }

  if (typeof original.email === 'string') {
    return `Actions for ${original.email}`;
  }

  return 'Open row actions menu';
};

const buildRowActionHook = (
  rowActions: DataTableRowAction[] | DataTableRowActionCallback,
) => {
  return (hooks: Hooks) => {
    hooks.visibleColumns.push((columns) => [
      ...columns,
      {
        id: 'rowActions',
        width: '1%',
        disableSortBy: true,
        Header: '',
        Cell: ({ row }) => {
          const realRowActions =
            typeof rowActions === 'function' ? rowActions(row) : rowActions;

          // No row actions
          if (!realRowActions || realRowActions.length === 0) {
            return null;
          }

          const rowActionsLabel = getRowActionsAriaLabel(row);

          // menu-landmark requires menus inside a nav (section is not enough)
          return (
            <Box as='nav' aria-label={rowActionsLabel}>
              <Menu placement='bottom-end'>
                <MenuButton
                  onClick={(event) => event.stopPropagation()}
                  as={IconButton}
                  aria-label={rowActionsLabel}
                  size='sm'
                  variant='ghost'
                  icon={<PortalIcon path={mdiDotsHorizontal} />}
                />
                <MenuList>
                  {realRowActions.map((ra) => (
                    <MenuItem
                      key={ra.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        ra.onClick(e, row);
                      }}
                      {...(ra['data-behavior-analytics-id'] && {
                        'data-behavior-analytics-id':
                          ra['data-behavior-analytics-id'],
                      })}
                    >
                      {ra.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>
          );
        },
      },
    ]);
  };
};

/**
 * Internal function for generating the checkbox selection column
 */
const selectableColumnHook = (hooks: Hooks) => {
  hooks.visibleColumns.push((columns) => [
    {
      id: 'selection',
      width: '1%',
      disableSortBy: true,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        const checkboxProps = getToggleAllRowsSelectedProps();
        // react-table includes a `key` prop in prop-getters; avoid spreading it to JSX
        const { key: __key, ...restCheckboxProps } = checkboxProps;
        void __key;
        return (
          <Checkbox
            isChecked={checkboxProps.checked}
            isIndeterminate={checkboxProps.indeterminate}
            {...restCheckboxProps}
            aria-label='Select all rows'
          />
        );
      },
      Cell: ({ row }: any) => {
        const checkboxProps = row.getToggleRowSelectedProps();
        // react-table includes a `key` prop in prop-getters; avoid spreading it to JSX
        const { key: __key, ...restCheckboxProps } = checkboxProps;
        void __key;
        return (
          <Checkbox
            isChecked={checkboxProps.checked}
            isIndeterminate={checkboxProps.indeterminate}
            {...restCheckboxProps}
            aria-label='Select row'
          />
        );
      },
    } as DataTableColumn,
    ...columns,
  ]);
};

const buildSelectableColumnHook = (
  hideSelectAll?: boolean,
  maxSelection?: number,
) => {
  return (hooks: Hooks) => {
    hooks.visibleColumns.push((columns) => [
      {
        id: 'selection',
        width: '1%',
        disableSortBy: true,
        Header: hideSelectAll
          ? () => null
          : ({ getToggleAllRowsSelectedProps }: any) => {
              const checkboxProps = getToggleAllRowsSelectedProps();
              // react-table includes a `key` prop in prop-getters; avoid spreading it to JSX
              const { key: __key, ...restCheckboxProps } = checkboxProps;
              void __key;
              return (
                <Checkbox
                  isChecked={checkboxProps.checked}
                  isIndeterminate={checkboxProps.indeterminate}
                  {...restCheckboxProps}
                  aria-label='Select all rows'
                />
              );
            },
        Cell: ({ row, rows, state }: any) => {
          const checkboxProps = row.getToggleRowSelectedProps();
          // react-table includes a `key` prop in prop-getters; avoid spreading it to JSX
          const { key: __key, ...restCheckboxProps } = checkboxProps;
          void __key;

          let isDisabled = false;
          if (maxSelection !== undefined && !row.isSelected) {
            const selectedCount = state?.selectedRowIds
              ? Object.keys(state.selectedRowIds).length
              : rows.filter((r: any) => r.isSelected).length;
            isDisabled = selectedCount >= maxSelection;
          }

          return (
            <Checkbox
              isChecked={checkboxProps.checked}
              isIndeterminate={checkboxProps.indeterminate}
              isDisabled={isDisabled}
              {...restCheckboxProps}
              aria-label='Select row'
            />
          );
        },
      } as DataTableColumn,
      ...columns,
    ]);
  };
};

const emptyHook = () => {
  return;
};

/**
 * Internal function for generating the expand/collapse column
 */
const buildExpandColumnHook = (
  getCanExpand?: (row: any) => boolean,
  renderExpandIcon?: (row: any) => React.ReactNode,
) => {
  return (hooks: Hooks) => {
    hooks.visibleColumns.push((columns) => [
      {
        id: 'expand',
        width: '1%',
        disableSortBy: true,
        Cell: ({ row }: any): React.ReactElement | null => {
          const canExpand = getCanExpand ? getCanExpand(row) : row.canExpand;
          if (!canExpand) {
            return null;
          }

          if (renderExpandIcon) {
            const icon = renderExpandIcon(row);
            return (icon as React.ReactElement) || null;
          }

          return (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                row.toggleRowExpanded();
              }}
              size='sm'
              variant='ghost'
              icon={
                <PortalIcon
                  path={row.isExpanded ? mdiChevronUp : mdiChevronDown}
                />
              }
              aria-expanded={row.isExpanded ? 'true' : 'false'}
              aria-label={row.isExpanded ? 'Collapse row' : 'Expand row'}
            />
          );
        },
      },
      ...columns,
    ]);
  };
};

/**
 * Sitecore UI DataTable component
 */
export const DataTable = React.memo(
  ({
    data,
    height,
    width,
    minWidth,
    maxWidth,
    gutterBottom = true,
    stickyHeader,
    columns,
    isLoading,
    totalItems = 0,
    size,
    variant,
    selectable,
    hideSelectAll,
    maxSelection,
    pagination,
    page,
    pageSize = 10,
    sortBy,
    rowActions,
    onClickRow,
    selectedRow,
    onChange,
    autoSort = true,
    theaderProps,
    paginationProps,
    alignPagination = 'left',
    getCanExpand,
    renderExpandIcon,
    getSubRows,
    expandedRowIds,
    getRowId,
    onSelectedRowIdsChange,
    regionLabel,
    'data-testid': dataTestId,
    'data-behavior-analytics-feature': dataBehaviorAnalyticsFeature,
    children,
  }: DataTableProps) => {
    const theme = useTheme();
    const tableRegionRef = React.useRef<HTMLDivElement>(null);
    const tableRegionLabel =
      regionLabel?.trim() ||
      dataBehaviorAnalyticsFeature?.trim() ||
      'Data table';
    const expandColumnPadding = theme.space[1]; // Typically 4px (0.25rem)
    const subRowIndentPerLevel = theme.space[6]; // Typically 24px (1.5rem) - indentation per depth level
    const subRowBasePadding = theme.space[5]; // Typically 20px (1.25rem) - base padding for sub-rows

    const totalPages = React.useMemo(() => {
      return Math.ceil(totalItems / pageSize);
    }, [totalItems, pageSize]);

    const initialState = React.useMemo(() => {
      if (!page) return;
      return {
        pageIndex: page - 1,
        sortBy,
      };
    }, [page, sortBy]);

    const hasExpandableRows = !!getCanExpand || !!getSubRows;

    const {
      state,
      selectedFlatRows,
      dispatch,
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      page: p,
      gotoPage,
      toggleRowExpanded,
    } = useTable(
      {
        autoResetSelectedRows: false,
        columns: columns as any,
        data,
        initialState,
        // When autoSort is enabled, react-table should sort internally.
        // When autoSort is disabled, consumers manage sorting externally (manual).
        manualSortBy: !autoSort,
        disableSortRemove: true,
        manualPagination: true,
        pageCount: totalPages,
        ...(getRowId && { getRowId }),
        ...(getSubRows && {
          getSubRows: (originalRow: any) => {
            const result = getSubRows(originalRow);
            return result || [];
          },
        }),
      },
      useSortBy,
      useExpanded,
      usePagination,
      hideColumnHook,
      selectable ? useRowSelect : emptyHook,
      selectable
        ? hideSelectAll || maxSelection
          ? buildSelectableColumnHook(hideSelectAll, maxSelection)
          : selectableColumnHook
        : emptyHook,
      hasExpandableRows
        ? buildExpandColumnHook(getCanExpand, renderExpandIcon)
        : emptyHook,
      rowActions ? buildRowActionHook(rowActions) : emptyHook,
    );

    // react-table's TableState type does not include the fields added by plugin
    // hooks (useRowSelect adds selectedRowIds, useSortBy adds sortBy, usePagination
    // adds pageIndex). We type the state explicitly using the known plugin shape.
    type PluginState = {
      pageIndex: number;
      sortBy: DataTableSortingRule[];
      selectedRowIds: Record<string, boolean>;
    };
    const {
      pageIndex,
      sortBy: sb,
      selectedRowIds: tableSelectedRowIds = {},
    } = state as unknown as PluginState;

    // Track the previous controlled page prop to distinguish an external prop
    // change (parent reset pagination) from an internal pageIndex change (user
    // clicked a pagination button). Both fire this single effect — we need to
    // know which one drove the change to avoid an onChange ↔ setPage loop.
    const prevPageRef = React.useRef(page);

    useEffect(() => {
      // The controlled `page` prop changed — the parent set a new page.
      // Sync react-table's internal pageIndex to match and do NOT call onChange,
      // because reporting the page back to the parent would cause a feedback loop.
      if (page !== prevPageRef.current) {
        prevPageRef.current = page;
        if (page !== undefined && page - 1 !== pageIndex) {
          gotoPage(page - 1);
        }
        return;
      }
      prevPageRef.current = page;

      // pageIndex changed because the user interacted with the pagination UI.
      // Notify the parent so it can update its own page state.
      if (!onChange || typeof onChange !== 'function') return;
      const newPage = pageIndex + 1;
      if ((!page || page === newPage) && (!sortBy || sb === sortBy)) return;
      onChange({ page: newPage, sortBy: sb });
    }, [pageIndex, page, sb, sortBy, onChange, gotoPage]);

    useEffect(() => {
      if (!onSelectedRowIdsChange) {
        return;
      }

      onSelectedRowIdsChange(tableSelectedRowIds ?? {});
    }, [onSelectedRowIdsChange, tableSelectedRowIds]);

    const selectedRowsCount = React.useMemo(() => {
      if (!selectable) {
        return 0;
      }

      return Object.keys(tableSelectedRowIds).length;
    }, [selectable, tableSelectedRowIds]);

    const clearSelection = React.useCallback(() => {
      // Use resetSelectedRows instead of toggleAllRowsSelected(false) because
      // toggleAllRowsSelected only removes rows visible on the CURRENT page
      // (via nonGroupedRowsById), leaving other pages' selections intact.
      // resetSelectedRows resets to initialState.selectedRowIds || {}, clearing ALL pages.
      // The useEffect watching tableSelectedRowIds handles notifying the parent.
      dispatch({ type: 'resetSelectedRows' });
    }, [dispatch]);

    const loadingRows = React.useMemo(() => {
      return Array.apply(null, Array(pageSize)).map((r, i) => (
        <Tr key={i}>
          {headerGroups[0].headers.map((c, ci) => (
            <Td key={`${i}_${ci}`} style={{ width: c.width }}>
              <Skeleton
                w='full'
                h={rowActions && rowActions.length ? '8' : '5'}
              />
            </Td>
          ))}
        </Tr>
      ));
    }, [pageSize, headerGroups, rowActions]);

    const tHeadProps = React.useMemo(() => {
      return stickyHeader
        ? {
            zIndex: 2,
            position: 'sticky' as any,
            top: 0,
            backgroundColor: 'white',
            ...theaderProps,
          }
        : { ...theaderProps };
    }, [stickyHeader, theaderProps]);

    const isClickableRow = !!onClickRow;

    return (
      <Box as='section' ref={tableRegionRef} aria-label={tableRegionLabel}>
        <Table
          {...getTableProps()}
          fontSize='md'
          size={size}
          variant={variant}
          mb={gutterBottom ? 6 : 0}
          height={height}
          width={width}
          minWidth={minWidth}
          maxWidth={maxWidth}
          data-testid={dataTestId}
          data-behavior-analytics-feature={dataBehaviorAnalyticsFeature}
        >
          <Thead {...tHeadProps}>
            {headerGroups.map((headerGroup) =>
              (() => {
                const headerGroupProps = headerGroup.getHeaderGroupProps();
                const { key: headerGroupKey, ...restHeaderGroupProps } =
                  headerGroupProps;
                return (
                  <Tr
                    key={headerGroupKey ?? headerGroup.id}
                    {...restHeaderGroupProps}
                  >
                    {headerGroup.headers.map((column: any) => {
                      const isExpandColumn = column.id === 'expand';
                      const paddingProps = isExpandColumn
                        ? { pl: 1, pr: 1, py: 2 }
                        : {};

                      const headerLabel =
                        typeof column.Header === 'string'
                          ? column.Header
                          : column.id;
                      const { key: columnKey, ...restHeaderProps } =
                        column.getHeaderProps();
                      const sortToggleProps = column.canSort
                        ? column.getSortByToggleProps()
                        : undefined;
                      const {
                        onClick: onSortToggle,
                        title: _sortTitle,
                        style: _sortStyle,
                        ...restSortToggleProps
                      } = sortToggleProps ?? {};

                      const sortIcon = column.isSorted ? (
                        column.isSortedDesc ? (
                          <PortalIcon
                            path={mdiArrowDown}
                            boxSize='4'
                            aria-hidden={true}
                          />
                        ) : (
                          <PortalIcon
                            path={mdiArrowUp}
                            boxSize='4'
                            aria-hidden={true}
                          />
                        )
                      ) : null;

                      return (
                        <Th
                          key={columnKey ?? column.id}
                          style={{ width: column.width }}
                          {...restHeaderProps}
                          {...paddingProps}
                          isNumeric={column.isNumeric}
                          scope='col'
                          {...(() => {
                            if (column.id === 'selection')
                              return { 'aria-label': 'Row selection' };
                            if (column.id === 'expand')
                              return { 'aria-label': 'Row expand' };
                            if (column.id === 'rowActions')
                              return { 'aria-label': 'Row actions' };
                            // Columns with an empty Header string have no accessible name.
                            // Fall back to the column id so axe td-has-header can associate
                            // data cells with a named column header.
                            if (
                              typeof column.Header === 'string' &&
                              !column.Header
                            )
                              return { 'aria-label': column.id };
                            return {};
                          })()}
                          {...(column.canSort && {
                            'aria-sort': column.isSorted
                              ? column.isSortedDesc
                                ? 'descending'
                                : 'ascending'
                              : 'none',
                          })}
                        >
                          {column.canSort ? (
                            <Flex
                              as='button'
                              type='button'
                              align='center'
                              justify={column.isNumeric ? 'end' : 'start'}
                              width='100%'
                              fontWeight='inherit'
                              fontSize='inherit'
                              lineHeight='inherit'
                              color='inherit'
                              bg='transparent'
                              border='none'
                              p={0}
                              cursor='pointer'
                              textAlign='inherit'
                              onClick={onSortToggle}
                              aria-label={`Sort by ${headerLabel}`}
                              {...restSortToggleProps}
                            >
                              {column.render('Header')}
                              {sortIcon}
                            </Flex>
                          ) : (
                            <Flex
                              align='center'
                              justify={column.isNumeric ? 'end' : 'start'}
                            >
                              {column.render('Header')}
                              {sortIcon}
                            </Flex>
                          )}
                        </Th>
                      );
                    })}
                  </Tr>
                );
              })(),
            )}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {isLoading
              ? loadingRows
              : p.map((row: any) => {
                  prepareRow(row);

                  // Expand row immediately if it should be expanded to prevent flickering during search
                  if (expandedRowIds && hasExpandableRows && row.depth === 0) {
                    const rowId = row.original?.sitecoreContextId;
                    if (rowId && expandedRowIds.has(rowId) && !row.isExpanded) {
                      toggleRowExpanded(row.id, true);
                    }
                  }

                  const isSelected = selectedRow === row.original;
                  const isRowChecked = !!row.isSelected;
                  const isHighlighted = isSelected || isRowChecked;
                  const isSubRow = row.depth > 0;
                  const isErrorOrLoadingRow =
                    (row.original as any)?.isError ||
                    (row.original as any)?.isLoading;

                  const rowProps = row.getRowProps();
                  const { key: __rowKey, ...restRowProps } = rowProps;
                  void __rowKey;

                  return (
                    <Tr
                      key={row.id}
                      {...restRowProps}
                      data-selected={isSelected ? 'true' : undefined}
                      data-row-checked={isRowChecked ? 'true' : undefined}
                      data-depth={row.depth}
                      onClick={() => onClickRow?.(row.original)}
                      bg={isHighlighted ? 'gray.100' : undefined}
                      style={{
                        cursor:
                          isClickableRow && !isErrorOrLoadingRow
                            ? 'pointer'
                            : 'default',
                      }}
                      {...(isClickableRow &&
                        !isErrorOrLoadingRow && {
                          _hover: {
                            bg: isHighlighted ? 'gray.100' : 'gray.50',
                          },
                        })}
                    >
                      {row.cells.map((cell: any) => {
                        // Only indent the Context ID column (first data column after expand column) for sub-rows
                        const isContextIdColumn =
                          cell.column.id === 'sitecoreContextId';
                        const isExpandColumn = cell.column.id === 'expand';
                        const shouldIndent = isSubRow && isContextIdColumn;

                        // Override padding for expand column to reduce spacing
                        const customStyle = isExpandColumn
                          ? {
                              width: cell.column.width,
                              paddingLeft: expandColumnPadding,
                              paddingRight: expandColumnPadding,
                            }
                          : {
                              width: cell.column.width,
                              paddingLeft: shouldIndent
                                ? `calc(${row.depth} * ${subRowIndentPerLevel} + ${subRowBasePadding})`
                                : undefined,
                            };

                        const cellProps = cell.getCellProps();
                        const { key: __cellKey, ...restCellProps } = cellProps;
                        void __cellKey;

                        return (
                          <Td
                            key={cell.column.id}
                            {...restCellProps}
                            style={customStyle}
                            isNumeric={cell.column.isNumeric}
                            {...(cell.column.id === 'selection' && {
                              onClick: (e: React.MouseEvent) =>
                                e.stopPropagation(),
                            })}
                          >
                            {cell.render('Cell')}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
          </Tbody>
        </Table>

        {pagination !== 'hidden' && (
          <Flex justify={alignPagination} data-testid='dataTable_pagination'>
            <Pagination
              variant={pagination}
              page={pageIndex + 1}
              totalItems={totalItems}
              pageSize={pageSize}
              onChange={(p: number) => {
                gotoPage(p - 1);
              }}
              {...paginationProps}
            />
          </Flex>
        )}
        {selectable &&
          selectedRowsCount > 0 &&
          typeof children === 'function' &&
          children({
            selectedRows: selectedFlatRows,
            clearSelection,
          })}
      </Box>
    );
  },
);

DataTable.displayName = 'DataTable';

// Export for testing
export { buildSelectableColumnHook, selectableColumnHook };
